import React, { useLayoutEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import Icon from 'src/components/HTKit/Icon';
import styles from './styles.scss';

const PREVIEW_LIMIT = 7;

/*
  Preview Button
*/
const PreviewButton = ({ item, goToSlide, slideIndex, index }) => {
  const { type, thumbnailSource } = item;
  const imgStyles = cn(styles.thumbnail, {
    [styles.inactive]: slideIndex !== index,
  });
  return (
    <button
      type="button"
      key={index}
      className={cn('plainButton', styles.thumbnailButton)}
      onClick={goToSlide(index)}
    >
      <img src={thumbnailSource} className={imgStyles} />
      {type === 'video' && (
        <div className={styles.iconWrapper}>
          <Icon name="play" className={styles.playIcon} />
        </div>
      )}
    </button>
  );
};

PreviewButton.propTypes = {
  item: PropTypes.object,
  goToSlide: PropTypes.func,
  slideIndex: PropTypes.number,
  index: PropTypes.number,
};

/*
  We're rolling our own because react-slick is a little unstable
*/
const Preview = ({ gallery = [], slideIndex, goToSlide }) => {
  const wrapperRef = useRef();
  const isOverLimit = gallery.length > PREVIEW_LIMIT;

  // If gallery count is over the limit, set the height to show only the limit
  useLayoutEffect(() => {
    if (isOverLimit) {
      /*
        We can't use last child's offsetTop because all of the images may not have
        loaded yet. Instead, we're just going to semi-hardcode the height.
      */
      const estimatedHeightOfBlock = 64 + 24; // assuming square image
      wrapperRef.current.style.height = `${estimatedHeightOfBlock * PREVIEW_LIMIT}px`;
    }
  }, []);

  // Scroll active image into view
  useLayoutEffect(() => {
    if (isOverLimit) {
      const kid = wrapperRef.current.children[slideIndex];
      const top = kid.offsetTop;
      wrapperRef.current.scrollTo({
        top: top - wrapperRef.current.offsetTop,
        behavior: 'smooth',
      });
    }
  }, [slideIndex]);

  // Function to call when chevron button is clicked
  const toggleSlide = (offset = 1) => () => {
    const galleryLength = gallery.length;
    let nextSlideIndex = slideIndex + offset;
    if (nextSlideIndex === -1) {
      nextSlideIndex = galleryLength - 1;
    } else if (nextSlideIndex === galleryLength) {
      nextSlideIndex = 0;
    }
    goToSlide(nextSlideIndex)();
  };

  const toggleStyles = cn('plainButton', styles.toggleButton);

  return (
    <div className={styles.previewContainer}>
      {isOverLimit && (
        <button type="button" onClick={toggleSlide(-1)} className={toggleStyles}>
          <Icon name="chevron-up" />
        </button>
      )}
      <div className={styles.thumbnailsWrapper} ref={wrapperRef}>
        {gallery.map((item, i) => (
          <PreviewButton
            item={item}
            goToSlide={goToSlide}
            slideIndex={slideIndex}
            index={i}
            key={i}
          />
        ))}
      </div>
      {isOverLimit && (
        <button type="button" onClick={toggleSlide()} className={toggleStyles}>
          <Icon name="chevron" />
        </button>
      )}
    </div>
  );
};

Preview.propTypes = {
  gallery: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.oneOf(['image', 'video']),
      thumbnailSource: PropTypes.string,
    }),
  ),
  slideIndex: PropTypes.number,
  goToSlide: PropTypes.func,
};

export default Preview;
